import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Users,
  DollarSign,
  TrendingUp,
  Link as LinkIcon,
  Copy,
  ExternalLink,
  Settings,
  Palette,
  BarChart3,
  CreditCard,
  Building2,
  Mail,
  Phone,
  Globe,
  Loader2,
  CheckCircle,
  Clock,
  AlertCircle,
  Banknote,
  ArrowRight
} from 'lucide-react';
import api from '../services/api';

const ResellerDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [whiteLabel, setWhiteLabel] = useState({});
  const [profile, setProfile] = useState({});
  const [referralLink, setReferralLink] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingWhiteLabel, setSavingWhiteLabel] = useState(false);
  
  // Stripe Connect state
  const [stripeConnectStatus, setStripeConnectStatus] = useState(null);
  const [loadingStripeConnect, setLoadingStripeConnect] = useState(false);
  const [requestingPayout, setRequestingPayout] = useState(false);
  const [payoutHistory, setPayoutHistory] = useState([]);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/reseller/dashboard');
      setDashboard(response.data);
      setProfile(response.data.profile || {});
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadReferrals = useCallback(async () => {
    try {
      const response = await api.get('/reseller/referrals?page=1&page_size=50');
      setReferrals(response.data.referrals || []);
    } catch (error) {
      console.error('Failed to load referrals:', error);
    }
  }, []);

  const loadCommissions = useCallback(async () => {
    try {
      const response = await api.get('/reseller/commissions?page=1&page_size=50');
      setCommissions(response.data.commissions || []);
    } catch (error) {
      console.error('Failed to load commissions:', error);
    }
  }, []);

  const loadAnalytics = useCallback(async () => {
    try {
      const response = await api.get('/reseller/analytics?period=30d');
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    }
  }, []);

  const loadWhiteLabel = useCallback(async () => {
    try {
      const response = await api.get('/reseller/white-label');
      setWhiteLabel(response.data || {});
    } catch (error) {
      console.error('Failed to load white-label config:', error);
    }
  }, []);

  const loadReferralLink = useCallback(async () => {
    try {
      const response = await api.get('/reseller/referral-link');
      setReferralLink(response.data.referral_link || '');
    } catch (error) {
      console.error('Failed to load referral link:', error);
    }
  }, []);
  
  const loadStripeConnectStatus = useCallback(async () => {
    setLoadingStripeConnect(true);
    try {
      const response = await api.get('/stripe-connect/status');
      setStripeConnectStatus(response.data);
    } catch (error) {
      console.error('Failed to load Stripe Connect status:', error);
    } finally {
      setLoadingStripeConnect(false);
    }
  }, []);
  
  const loadPayoutHistory = useCallback(async () => {
    try {
      const response = await api.get('/stripe-connect/payout-history');
      setPayoutHistory(response.data.payouts || []);
    } catch (error) {
      console.error('Failed to load payout history:', error);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
    loadReferralLink();
    loadStripeConnectStatus();
  }, [loadDashboard, loadReferralLink, loadStripeConnectStatus]);

  useEffect(() => {
    if (activeTab === 'referrals') loadReferrals();
    if (activeTab === 'commissions') loadCommissions();
    if (activeTab === 'analytics') loadAnalytics();
    if (activeTab === 'white-label') loadWhiteLabel();
    if (activeTab === 'payouts') { loadStripeConnectStatus(); loadPayoutHistory(); }
  }, [activeTab, loadReferrals, loadCommissions, loadAnalytics, loadWhiteLabel, loadStripeConnectStatus, loadPayoutHistory]);

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.put('/reseller/profile', profile);
      toast.success('Profile updated');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveWhiteLabel = async () => {
    setSavingWhiteLabel(true);
    try {
      await api.put('/reseller/white-label', whiteLabel);
      toast.success('White-label settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save white-label settings');
    } finally {
      setSavingWhiteLabel(false);
    }
  };
  
  const handleConnectStripe = async () => {
    setLoadingStripeConnect(true);
    try {
      const origin = window.location.origin;
      const response = await api.post('/stripe-connect/onboard', {
        return_url: `${origin}/dashboard/reseller?tab=payouts&stripe_connected=true`,
        refresh_url: `${origin}/dashboard/reseller?tab=payouts&stripe_refresh=true`
      });
      
      if (response.data.status === 'already_onboarded') {
        toast.success('Your payout account is already set up!');
        loadStripeConnectStatus();
      } else if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start Stripe onboarding');
    } finally {
      setLoadingStripeConnect(false);
    }
  };
  
  const handleRequestPayout = async () => {
    setRequestingPayout(true);
    try {
      const response = await api.post('/stripe-connect/request-payout');
      toast.success(`Payout request submitted: $${response.data.amount.toFixed(2)}`);
      loadDashboard();
      loadPayoutHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request payout');
    } finally {
      setRequestingPayout(false);
    }
  };
  
  const handleOpenStripeDashboard = async () => {
    try {
      const response = await api.get('/stripe-connect/dashboard-link');
      if (response.data.url) {
        window.open(response.data.url, '_blank');
      }
    } catch (error) {
      toast.error('Failed to open Stripe dashboard');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const stats = dashboard?.stats || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reseller Dashboard</h1>
          <p className="text-slate-600">Manage your referrals, commissions, and white-label settings</p>
        </div>
        <Badge className="bg-purple-100 text-purple-700 border-purple-200">Reseller Partner</Badge>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-white border-slate-200">
          <CardContent className="pt-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-slate-600">Total Referrals</p>
                <p className="text-3xl font-bold text-slate-900">{stats.total_referrals || 0}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-xl">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2">{stats.active_customers || 0} active customers</p>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200">
          <CardContent className="pt-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-slate-600">Total Earned</p>
                <p className="text-3xl font-bold text-green-600">${(stats.total_earned || 0).toFixed(2)}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-xl">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2">{stats.commission_rate || 20}% commission rate</p>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200">
          <CardContent className="pt-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-slate-600">Pending Payout</p>
                <p className="text-3xl font-bold text-amber-600">${(stats.pending_amount || 0).toFixed(2)}</p>
              </div>
              <div className="p-3 bg-amber-100 rounded-xl">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2">Paid monthly</p>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200">
          <CardContent className="pt-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-slate-600">This Month</p>
                <p className="text-3xl font-bold text-purple-600">${(stats.this_month_earned || 0).toFixed(2)}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-xl">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2">Commission earned</p>
          </CardContent>
        </Card>
      </div>

      {/* Referral Link Card */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <LinkIcon className="w-5 h-5 text-blue-600" />
                Your Referral Link
              </h3>
              <p className="text-sm text-slate-600 mt-1">Share this link with potential customers to earn commission</p>
            </div>
            <div className="flex flex-col gap-2 w-full md:w-auto">
              <div className="flex gap-2">
                <Input 
                  value={referralLink} 
                  readOnly 
                  className="bg-white border-slate-300 flex-1 md:w-80 text-sm"
                />
                <Button onClick={() => copyToClipboard(referralLink)} className="bg-blue-600 hover:bg-blue-700">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="referrals">Referrals</TabsTrigger>
          <TabsTrigger value="commissions">Commissions</TabsTrigger>
          <TabsTrigger value="payouts">Payouts</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="white-label">White-Label</TabsTrigger>
          <TabsTrigger value="profile">Profile</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="text-slate-900">Recent Referrals</CardTitle>
            </CardHeader>
            <CardContent>
              {(dashboard?.recent_referrals || []).length === 0 ? (
                <p className="text-slate-500 text-center py-8">No referrals yet. Share your referral link to get started!</p>
              ) : (
                <div className="space-y-3">
                  {(dashboard?.recent_referrals || []).slice(0, 5).map((ref) => (
                    <div key={ref.user_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium text-slate-900">{ref.name || ref.email}</p>
                        <p className="text-sm text-slate-500">{ref.email}</p>
                      </div>
                      <Badge className={ref.is_suspended ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>
                        {ref.is_suspended ? 'Suspended' : 'Active'}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Referrals Tab */}
        <TabsContent value="referrals">
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="text-slate-900">All Referrals</CardTitle>
            </CardHeader>
            <CardContent>
              {referrals.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No referrals yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Customer</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Plan</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Status</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {referrals.map((ref) => (
                        <tr key={ref.user_id} className="border-t border-slate-100">
                          <td className="p-3">
                            <p className="font-medium text-slate-900">{ref.name || 'Unknown'}</p>
                            <p className="text-sm text-slate-500">{ref.email}</p>
                          </td>
                          <td className="p-3">
                            <Badge variant="outline" className="capitalize">
                              {ref.subscription?.tier || 'free'}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <Badge className={ref.is_suspended ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>
                              {ref.is_suspended ? 'Suspended' : 'Active'}
                            </Badge>
                          </td>
                          <td className="p-3 text-sm text-slate-500">
                            {ref.created_at ? new Date(ref.created_at).toLocaleDateString() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Commissions Tab */}
        <TabsContent value="commissions">
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="text-slate-900">Commission History</CardTitle>
            </CardHeader>
            <CardContent>
              {commissions.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No commissions yet. Referrals that convert to paid plans will appear here.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Date</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Customer</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Payment</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Commission</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {commissions.map((comm) => (
                        <tr key={comm.commission_id} className="border-t border-slate-100">
                          <td className="p-3 text-sm text-slate-600">
                            {comm.created_at ? new Date(comm.created_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="p-3 text-sm text-slate-900">{comm.customer_id}</td>
                          <td className="p-3 text-sm text-slate-600">${(comm.payment_amount || 0).toFixed(2)}</td>
                          <td className="p-3 font-medium text-green-600">${(comm.amount || 0).toFixed(2)}</td>
                          <td className="p-3">
                            <Badge className={
                              comm.status === 'paid' ? 'bg-green-100 text-green-700' :
                              comm.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-700'
                            }>
                              {comm.status}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payouts Tab - Stripe Connect */}
        <TabsContent value="payouts">
          <div className="space-y-6">
            {/* Stripe Connect Status Card */}
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-slate-900 flex items-center gap-2">
                  <Banknote className="w-5 h-5 text-green-600" />
                  Payout Account
                </CardTitle>
                <CardDescription>Connect your Stripe account to receive commission payouts</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {loadingStripeConnect ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                  </div>
                ) : stripeConnectStatus?.connected && stripeConnectStatus?.payouts_enabled ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                      <div>
                        <p className="font-medium text-green-800">Stripe Account Connected</p>
                        <p className="text-sm text-green-600">Your account is ready to receive payouts</p>
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-500">Pending Balance</p>
                        <p className="text-2xl font-bold text-amber-600">${(stats.pending_amount || 0).toFixed(2)}</p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-500">Total Paid Out</p>
                        <p className="text-2xl font-bold text-green-600">${(stats.total_paid || 0).toFixed(2)}</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      <Button 
                        onClick={handleRequestPayout}
                        disabled={requestingPayout || (stats.pending_amount || 0) < 50}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {requestingPayout ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <DollarSign className="w-4 h-4 mr-2" />}
                        Request Payout
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={handleOpenStripeDashboard}
                        className="border-slate-300"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Open Stripe Dashboard
                      </Button>
                    </div>
                    
                    {(stats.pending_amount || 0) < 50 && (
                      <p className="text-sm text-slate-500">
                        Minimum payout amount is $50. Current balance: ${(stats.pending_amount || 0).toFixed(2)}
                      </p>
                    )}
                  </div>
                ) : stripeConnectStatus?.connected ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <AlertCircle className="w-6 h-6 text-amber-600" />
                      <div>
                        <p className="font-medium text-amber-800">Account Setup Incomplete</p>
                        <p className="text-sm text-amber-600">Please complete your Stripe account setup to receive payouts</p>
                      </div>
                    </div>
                    <Button onClick={handleConnectStripe} disabled={loadingStripeConnect} className="bg-blue-600 hover:bg-blue-700">
                      {loadingStripeConnect ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Complete Setup
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg">
                      <h4 className="font-medium text-slate-900 mb-2">Connect with Stripe</h4>
                      <p className="text-sm text-slate-600 mb-4">
                        Set up your payout account with Stripe to receive your commissions directly to your bank account.
                      </p>
                      <ul className="text-sm text-slate-600 space-y-1 mb-4">
                        <li>• Secure payouts to your bank account</li>
                        <li>• Track all your earnings in one place</li>
                        <li>• Fast setup process (under 5 minutes)</li>
                      </ul>
                      <Button onClick={handleConnectStripe} disabled={loadingStripeConnect} className="bg-purple-600 hover:bg-purple-700">
                        {loadingStripeConnect ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
                        Connect Stripe Account
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Payout History */}
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-slate-900">Payout History</CardTitle>
              </CardHeader>
              <CardContent>
                {payoutHistory.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No payouts yet</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left p-3 text-sm font-medium text-slate-600">Date</th>
                          <th className="text-left p-3 text-sm font-medium text-slate-600">Amount</th>
                          <th className="text-left p-3 text-sm font-medium text-slate-600">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payoutHistory.map((payout) => (
                          <tr key={payout.payout_id} className="border-t border-slate-100">
                            <td className="p-3 text-sm text-slate-600">
                              {payout.created_at ? new Date(payout.created_at).toLocaleDateString() : '-'}
                            </td>
                            <td className="p-3 font-medium text-green-600">${(payout.amount || 0).toFixed(2)}</td>
                            <td className="p-3">
                              <Badge className={
                                payout.status === 'completed' ? 'bg-green-100 text-green-700' :
                                payout.status === 'requested' ? 'bg-amber-100 text-amber-700' :
                                payout.status === 'failed' ? 'bg-red-100 text-red-700' :
                                'bg-slate-100 text-slate-700'
                              }>
                                {payout.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-slate-900">Referral Trend (30 days)</CardTitle>
              </CardHeader>
              <CardContent>
                {analytics?.referral_trend?.length > 0 ? (
                  <div className="space-y-2">
                    {analytics.referral_trend.map((item) => (
                      <div key={item.date} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                        <span className="text-sm text-slate-600">{item.date}</span>
                        <Badge>{item.count} referrals</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No data available</p>
                )}
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-slate-900">Customer Tiers</CardTitle>
              </CardHeader>
              <CardContent>
                {analytics?.tier_breakdown ? (
                  <div className="space-y-3">
                    {Object.entries(analytics.tier_breakdown).map(([tier, count]) => (
                      <div key={tier} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                        <span className="font-medium text-slate-700 capitalize">{tier}</span>
                        <Badge variant="outline">{count} customers</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No data available</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* White-Label Tab */}
        <TabsContent value="white-label">
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="text-slate-900 flex items-center gap-2">
                <Palette className="w-5 h-5" />
                White-Label Settings
              </CardTitle>
              <CardDescription>Customize the platform with your own branding</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900">Enable White-Label</p>
                  <p className="text-sm text-slate-500">Show your branding instead of Harmonix</p>
                </div>
                <Button
                  variant={whiteLabel.enabled ? 'default' : 'outline'}
                  onClick={() => setWhiteLabel({ ...whiteLabel, enabled: !whiteLabel.enabled })}
                  className={whiteLabel.enabled ? 'bg-blue-600' : ''}
                >
                  {whiteLabel.enabled ? 'Enabled' : 'Disabled'}
                </Button>
              </div>

              {whiteLabel.enabled && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-700">Brand Name</Label>
                    <Input
                      value={whiteLabel.brand_name || ''}
                      onChange={(e) => setWhiteLabel({ ...whiteLabel, brand_name: e.target.value })}
                      placeholder="Your Company Name"
                      className="bg-white border-slate-300"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-700">Logo URL</Label>
                    <Input
                      value={whiteLabel.logo_url || ''}
                      onChange={(e) => setWhiteLabel({ ...whiteLabel, logo_url: e.target.value })}
                      placeholder="https://yoursite.com/logo.png"
                      className="bg-white border-slate-300"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-700">Primary Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={whiteLabel.primary_color || '#6366f1'}
                        onChange={(e) => setWhiteLabel({ ...whiteLabel, primary_color: e.target.value })}
                        className="w-16 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={whiteLabel.primary_color || '#6366f1'}
                        onChange={(e) => setWhiteLabel({ ...whiteLabel, primary_color: e.target.value })}
                        className="bg-white border-slate-300 flex-1"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-700">Support Email</Label>
                    <Input
                      value={whiteLabel.support_email || ''}
                      onChange={(e) => setWhiteLabel({ ...whiteLabel, support_email: e.target.value })}
                      placeholder="support@yourcompany.com"
                      className="bg-white border-slate-300"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-slate-700">Custom Domain</Label>
                    <Input
                      value={whiteLabel.custom_domain || ''}
                      onChange={(e) => setWhiteLabel({ ...whiteLabel, custom_domain: e.target.value })}
                      placeholder="chat.yourcompany.com"
                      className="bg-white border-slate-300"
                    />
                    <p className="text-xs text-slate-500">Point your domain CNAME to chat.harmonix.info</p>
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <Button onClick={handleSaveWhiteLabel} disabled={savingWhiteLabel} className="bg-blue-600 hover:bg-blue-700">
                  {savingWhiteLabel ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="text-slate-900 flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Reseller Profile
              </CardTitle>
              <CardDescription>Your business information for payouts and communication</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-700">Company Name</Label>
                  <Input
                    value={profile.company_name || ''}
                    onChange={(e) => setProfile({ ...profile, company_name: e.target.value })}
                    placeholder="Your Company LLC"
                    className="bg-white border-slate-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700">Website</Label>
                  <Input
                    value={profile.website || ''}
                    onChange={(e) => setProfile({ ...profile, website: e.target.value })}
                    placeholder="https://yourcompany.com"
                    className="bg-white border-slate-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700">Contact Email</Label>
                  <Input
                    type="email"
                    value={profile.contact_email || ''}
                    onChange={(e) => setProfile({ ...profile, contact_email: e.target.value })}
                    placeholder="contact@yourcompany.com"
                    className="bg-white border-slate-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700">Contact Phone</Label>
                  <Input
                    value={profile.contact_phone || ''}
                    onChange={(e) => setProfile({ ...profile, contact_phone: e.target.value })}
                    placeholder="+1 (555) 000-0000"
                    className="bg-white border-slate-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700">Tax ID / VAT Number</Label>
                  <Input
                    value={profile.tax_id || ''}
                    onChange={(e) => setProfile({ ...profile, tax_id: e.target.value })}
                    placeholder="Optional"
                    className="bg-white border-slate-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700">Payout Method</Label>
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                    Payouts are handled via <strong>Stripe Connect</strong>. Go to the <strong>Payouts</strong> tab to set up or manage your payout account.
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={handleSaveProfile} disabled={savingProfile} className="bg-blue-600 hover:bg-blue-700">
                  {savingProfile ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save Profile
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ResellerDashboard;
